/**
 * The whole relationship between the Format tab's "Legend position" picker and
 * Vega-Lite's `legend.orient`, in one place. The writer and the reader both go
 * through here so the mapping and its inverse cannot drift.
 *
 * The rule that shapes the picker: only Vega's four EDGE orients
 * ("left"/"right"/"top"/"bottom") dock a legend outside the plot and reserve
 * layout space for it. Its four CORNER orients ("top-left", "bottom-right",
 * ...) are floating insets drawn over the marks — `ViewLayout`'s autosize-fit
 * branch has no case for them — and `ChartWidget` renders with
 * `autosize: {type: "fit"|"fit-x"}`, so a corner orient is guaranteed to land
 * the legend on top of the bars.
 *
 * So the panel offers exactly those four edges as ONE picker. It used to be two
 * pickers, left/centre/right over top/middle/bottom, which read as two
 * independent axes but could only ever describe one edge: "centre" and "middle"
 * were inert placeholders, picking one group silently reset the other, and the
 * pair had to be policed so it could not combine into a corner. One button, one
 * orient removes all three problems.
 */

/** Every position the picker offers — each id IS the Vega orient it writes. */
export const LEGEND_POSITIONS = ["left", "right", "top", "bottom"] as const;

export type LegendPosition = (typeof LEGEND_POSITIONS)[number];

/**
 * What Vega-Lite does when a spec states no `legend.orient`. The registry
 * default and the reader's fallback both use it, so the highlighted button
 * always describes the legend actually on screen — including on a chart that
 * has never been formatted. The old default pair said "bottom" while Vega drew
 * the legend on the right, which is how a picked position could be a no-op.
 */
export const DEFAULT_LEGEND_POSITION: LegendPosition = "right";

export const isLegendPosition = (value: string): value is LegendPosition =>
  (LEGEND_POSITIONS as readonly string[]).includes(value);

/**
 * Picker value → Vega orient. Identity by construction; the fallback only
 * catches a value from outside the picker (a spec or a stored entry written
 * before this became one picker), which must never reach the spec as an orient
 * Vega would reject.
 */
export const legendOrient = (position: string): LegendPosition =>
  isLegendPosition(position) ? position : DEFAULT_LEGEND_POSITION;

/**
 * Vega orient → picker value. A corner orient collapses to its side, which is
 * both how the writer would re-emit it and how the user gets a legend they can
 * actually read: a spec saved with an inset legend heals to a docked one. An
 * orient with no edge to collapse to — `"none"`, which means "positioned by
 * `legendX`/`legendY`" — reports nothing, and the caller keeps its default.
 */
export const legendPositionFromOrient = (
  orient: string,
): LegendPosition | undefined => {
  if (isLegendPosition(orient)) return orient;

  const [, side] = orient.split("-");
  return side && isLegendPosition(side) ? side : undefined;
};
